import { useCallback, useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IWhatsAppTemplateFormV0 } from './useWhatsAppTemplateForm';

import { TRANSLATE_WHATSAPP_ACTION_FORM } from '@/modules/CampaignsModule/constants';
import { UrlAction, WHATSAPP_ACTION_TYPES } from '@/modules/CampaignsModule/interfaces/WhatsApp';

interface ActionButton {
  id: string;
  name: string;
}

interface FormAction {
  title: string;
  url: string;
}

interface UseWhatsAppActionsProps {
  setValue: UseFormSetValue<IWhatsAppTemplateFormV0>;
}

export const useWhatsAppActions = ({ setValue }: UseWhatsAppActionsProps) => {
  const { t } = useTranslation('inbox');
  const [actionsButton, setActionsButton] = useState<ActionButton[]>([]);
  const [actions, setActions] = useState<UrlAction[]>([]);

  const countUrlButtons = useCallback(() => {
    return actions.filter((action) => action.type === WHATSAPP_ACTION_TYPES.URL).length;
  }, [actions]);

  const handleChangeActionField = useCallback(
    (index: number, field: string, value: string) => {
      const updatedActions = [...actions];

      if (field === 'title' || field === 'url') {
        updatedActions[index] = {
          ...updatedActions[index],
          [field]: value,
        };
      }

      setActions(updatedActions);

      const formActions: FormAction[] = updatedActions.map((action) => ({
        title: action.title,
        url: action.url,
      }));

      setValue('actions', formActions);
    },
    [actions, setValue]
  );

  const addActionButton = useCallback(() => {
    if (countUrlButtons() < 2) {
      const newButton = {
        id: `button-${actionsButton.length + 1}`,
        name: `${t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.button`)} ${actionsButton.length + 1}`,
      };
      setActionsButton((prev) => [...prev, newButton]);

      const newAction: UrlAction = {
        title: '',
        type: WHATSAPP_ACTION_TYPES.URL,
        url: '',
      };

      setActions((prev) => [...prev, newAction]);

      const updatedActions = [...actions, newAction];

      const formActions: FormAction[] = updatedActions.map((action) => ({
        title: action.title,
        url: action.url,
      }));

      setValue('actions', formActions);
    }
  }, [actionsButton, t, actions, setValue, countUrlButtons]);

  const removeActionButton = useCallback(
    (id: string) => {
      const indexToRemove = actionsButton.findIndex((button) => button.id === id);
      if (indexToRemove !== -1) {
        setActionsButton((prev) => prev.filter((button) => button.id !== id));

        const updatedActions = [...actions];
        updatedActions.splice(indexToRemove, 1);
        setActions(updatedActions);

        const formActions: FormAction[] = updatedActions.map((action) => ({
          title: action.title,
          url: action.url,
        }));

        setValue('actions', formActions);
      }
    },
    [actionsButton, actions, setValue]
  );

  useEffect(() => {
    if (actionsButton.length > actions.length) {
      const newActions = [...actions];
      for (let i = actions.length; i < actionsButton.length; i++) {
        newActions.push({
          title: '',
          type: WHATSAPP_ACTION_TYPES.URL,
          url: '',
        });
      }
      setActions(newActions);
    } else if (actionsButton.length < actions.length) {
      setActions((prev) => prev.slice(0, actionsButton.length));
    }
  }, [actionsButton, actions]);

  return {
    actionsButton,
    actions,
    handleChangeActionField,
    addActionButton,
    removeActionButton,
    BUTTON_TYPE: WHATSAPP_ACTION_TYPES,
  };
};

export default useWhatsAppActions;
